import { useState, useEffect, useCallback } from 'react';
import { getPreciosEmpresa, updatePreciosEmpresaMasivo } from '@shared/helpers/api/mantenedores/tarifarios';
import { getEmpresas } from '@shared/helpers/api/mantenedores/empresas';
import { useGeneralesCrud } from './useGeneralesCrud';

/**
 * Hook para manejar la gestión de tarifarios de empresas
 */
export const useTarifariosEmpresasMantenedor = (user, permisos = {}) => {
    // Estados principales
    const [preciosEmpresas, setPreciosEmpresas] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Estados de paginación
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);

    // Estados de filtros aplicados (los que realmente se usan en la consulta)
    const [appliedFilters, setAppliedFilters] = useState({
        servicioId: '',
        empresaId: '',
        regionId: '',
        comunaId: ''
    });

    // Estados de filtros temporales (los que se muestran en el modal pero no se aplican hasta confirmar)
    const [servicioId, setServicioId] = useState('');
    const [empresaId, setEmpresaId] = useState('');
    const [regionId, setRegionId] = useState('');
    const [comunaId, setComunaId] = useState('');

    // Estados para opciones de filtros
    const [empresas, setEmpresas] = useState([]);
    const [loadingEmpresas, setLoadingEmpresas] = useState(false);
    const [comunasFiltradas, setComunasFiltradas] = useState([]);

    // Estados para actualización masiva
    const [updating, setUpdating] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    // Hooks para datos generales usando useGeneralesCrud
    const serviciosHook = useGeneralesCrud('servicios-spread', user, { canView: true });
    const regionesHook = useGeneralesCrud('regiones', user, { canView: true });
    const comunasHook = useGeneralesCrud('comunas', user, { canView: true });

    // Cargar empresas al montar el componente
    useEffect(() => {
        const loadEmpresas = async () => {
            if (!user) return;

            setLoadingEmpresas(true);
            try {
                const response = await getEmpresas(user);
                if (response.success) {
                    setEmpresas(response.data || []);
                } else {
                    console.error('Error al cargar empresas:', response.error);
                    setEmpresas([]);
                }
            } catch (error) {
                console.error('Error al cargar empresas:', error);
                setEmpresas([]);
            } finally {
                setLoadingEmpresas(false);
            }
        };

        loadEmpresas();
    }, [user]);

    // Efecto para filtrar comunas cuando cambia la región seleccionada en el modal
    useEffect(() => {
        const todasLasComunas = comunasHook.allData || [];
        
        if (regionId) {
            // Filtrar comunas por región
            const comunasDeLaRegion = todasLasComunas.filter(comuna => 
                comuna.region && comuna.region.id.toString() === regionId.toString()
            );
            setComunasFiltradas(comunasDeLaRegion);
            
            // Si la comuna actual no pertenece a la región seleccionada, limpiarla
            if (comunaId) {
                const comunaActual = comunasDeLaRegion.find(comuna => 
                    comuna.id.toString() === comunaId.toString()
                );
                if (!comunaActual) {
                    setComunaId('');
                }
            }
        } else {
            // Si no hay región seleccionada, mostrar todas las comunas
            setComunasFiltradas(todasLasComunas);
        }
    }, [regionId, comunasHook.allData, comunaId]);

    // Función para cargar precios de empresas
    const loadPreciosEmpresas = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        setError(null);

        try {
            // Construir parámetros de consulta usando los filtros aplicados
            const params = new URLSearchParams();
            params.append('pageSize', pageSize.toString());
            params.append('pageNumber', pageNumber.toString());

            if (appliedFilters.servicioId) params.append('servicio_id', appliedFilters.servicioId);
            if (appliedFilters.empresaId) params.append('empresa_id', appliedFilters.empresaId);
            if (appliedFilters.regionId) params.append('region_id', appliedFilters.regionId);
            if (appliedFilters.comunaId) params.append('comuna_id', appliedFilters.comunaId);

            const response = await getPreciosEmpresa(user, params.toString());

            if (response.success) {
                setPreciosEmpresas(response.data || []);
                setTotal(response.pagination?.total || 0);
            } else {
                setError(response.error || 'Error al cargar precios de empresas');
                setPreciosEmpresas([]);
                setTotal(0);
            }
        } catch (error) {
            console.error('Error al cargar precios de empresas:', error);
            setError('Error al cargar precios de empresas');
            setPreciosEmpresas([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [user, pageSize, pageNumber, appliedFilters.servicioId, appliedFilters.empresaId, appliedFilters.regionId, appliedFilters.comunaId]);

    // Cargar datos cuando cambien los parámetros
    useEffect(() => {
        loadPreciosEmpresas();
    }, [loadPreciosEmpresas]);

    // Función para actualizar precios masivamente
    const updatePreciosMasivo = async (preciosActualizados) => {
        if (!user || !preciosActualizados || preciosActualizados.length === 0) {
            return { success: false, error: 'Datos inválidos para actualización' };
        }

        setUpdating(true);
        setUpdateError(null);

        try {
            const response = await updatePreciosEmpresaMasivo(user, preciosActualizados);
            
            if (response.success) {
                // Recargar datos después de la actualización
                await loadPreciosEmpresas();
                return { success: true, data: response.data };
            } else {
                setUpdateError(response.error || 'Error al actualizar precios');
                return { success: false, error: response.error };
            }
        } catch (error) {
            console.error('Error al actualizar precios masivo:', error);
            setUpdateError('Error al actualizar precios');
            return { success: false, error: 'Error al actualizar precios' };
        } finally {
            setUpdating(false);
        }
    };

    // Handlers para cambios de paginación
    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPageNumber(1); // Reset a primera página
    };

    // Handlers para cambios de filtros temporales (no se aplican automáticamente)
    const handleServicioChange = (newServicioId) => {
        setServicioId(newServicioId);
    };

    const handleEmpresaChange = (newEmpresaId) => {
        setEmpresaId(newEmpresaId);
    };

    const handleRegionChange = (newRegionId) => {
        setRegionId(newRegionId);
        setComunaId(''); // Limpiar comuna cuando cambia región
    };

    const handleComunaChange = (newComunaId) => {
        setComunaId(newComunaId);
    };

    // Función para aplicar filtros (se llama desde el modal)
    const applyFilters = (filters) => {
        setAppliedFilters({
            servicioId: filters.servicioId || '',
            empresaId: filters.empresaId || '',
            regionId: filters.regionId || '',
            comunaId: filters.comunaId || ''
        });
        
        // También actualizar los estados temporales para mantener sincronía
        setServicioId(filters.servicioId || '');
        setEmpresaId(filters.empresaId || '');
        setRegionId(filters.regionId || '');
        setComunaId(filters.comunaId || '');
        
        setPageNumber(1); // Reset a primera página
    };

    // Función para limpiar filtros
    const clearFilters = () => {
        const filtrosVacios = {
            servicioId: '',
            empresaId: '',
            regionId: '',
            comunaId: ''
        };
        
        // Limpiar tanto los aplicados como los temporales
        setAppliedFilters(filtrosVacios);
        setServicioId('');
        setEmpresaId('');
        setRegionId('');
        setComunaId('');
        setPageNumber(1);
    };

    // Función para refrescar datos
    const refreshData = () => {
        loadPreciosEmpresas();
    };

    // Limpiar errores
    const clearError = () => {
        setError(null);
    };

    const clearUpdateError = () => {
        setUpdateError(null);
    };

    return {
        // Datos principales
        preciosEmpresas,
        total,
        loading,
        error,

        // Paginación
        pageSize,
        pageNumber,
        handlePageChange,
        handlePageSizeChange,

        // Filtros
        servicioId,
        empresaId,
        regionId,
        comunaId,
        appliedFilters,
        handleServicioChange,
        handleEmpresaChange,
        handleRegionChange,
        handleComunaChange,
        applyFilters,
        clearFilters,

        // Opciones para filtros
        empresas,
        loadingEmpresas,
        servicios: serviciosHook.allData || [],
        regiones: regionesHook.allData || [],
        comunas: comunasFiltradas,

        // Actualización masiva
        updating,
        updateError,
        updatePreciosMasivo,
        clearUpdateError,

        // Utilidades
        refreshData,
        clearError
    };
};
